import { useState, useEffect } from 'react'
import {
  Calendar,
  PlusCircle,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format } from 'date-fns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { reviewService } from '@/services/api/reviewService'
import { serverReason } from '@/lib/serverReason'
import toast from 'react-hot-toast'

interface RequestJITCheckinDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeId: string
  managerId: string
  managerName: string
  onSuccess?: () => void
}

export default function RequestJITCheckinDialog({
  open,
  onOpenChange,
  employeeId,
  managerId,
  managerName,
  onSuccess
}: RequestJITCheckinDialogProps) {
  // Default to 'adhoc' (General Check-in) - no dropdown needed
  const jitReviewType = 'adhoc'
  const [jitDescription, setJitDescription] = useState<string>('')
  const [jitScheduledDate, setJitScheduledDate] = useState<Date | undefined>(undefined)
  const [jitScheduledTime, setJitScheduledTime] = useState<string>('')
  const [jitDuration, setJitDuration] = useState<number>(30)
  const [jitAgenda, setJitAgenda] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Availability checking state
  const [jitIsCheckingAvailability, setJitIsCheckingAvailability] = useState(false)
  const [jitAvailabilityStatus, setJitAvailabilityStatus] = useState<'available' | 'conflict' | 'unknown'>('unknown')
  const [jitConflicts, setJitConflicts] = useState<Array<{ email: string; employee_id?: string; employee_ids?: string[]; busy_items: any[] }>>([])

  // Check availability when date/time changes
  useEffect(() => {
    const checkJitAvailability = async () => {
      if (!jitScheduledDate || !jitScheduledTime || !employeeId || !managerId) {
        setJitAvailabilityStatus('unknown')
        return
      }
      
      try {
        setJitIsCheckingAvailability(true)
        
        // Combine date and time
        const [hours, minutes] = jitScheduledTime.split(':').map(Number)
        const dateTime = new Date(jitScheduledDate)
        dateTime.setHours(hours, minutes, 0, 0)
        
        const result = await reviewService.checkAvailability(
          [employeeId, managerId],
          dateTime,
          jitDuration,
          null // No existing event to exclude
        )
        
        if (result.error) {
          setJitAvailabilityStatus('unknown')
        } else if (result.available) {
          setJitAvailabilityStatus('available')
          setJitConflicts([])
        } else {
          setJitAvailabilityStatus('conflict')
          setJitConflicts(result.conflicts || [])
        }
      } catch (error) {
        console.error('Error checking JIT availability:', error)
        setJitAvailabilityStatus('unknown')
      } finally {
        setJitIsCheckingAvailability(false)
      }
    }
    
    const timeoutId = setTimeout(checkJitAvailability, 500)
    return () => clearTimeout(timeoutId)
  }, [jitScheduledDate, jitScheduledTime, jitDuration, employeeId, managerId])

  const resetForm = () => {
    setJitDescription('')
    setJitScheduledDate(undefined)
    setJitScheduledTime('')
    setJitDuration(30)
    setJitAgenda('')
    setJitAvailabilityStatus('unknown')
    setJitConflicts([])
  }

  const handleSubmit = async () => {
    if (!jitScheduledDate || !jitScheduledTime) {
      toast.error('Please select a date and time')
      return
    }

    try {
      setIsSubmitting(true)
      
      // Combine date and time for scheduled date
      const [hours, minutes] = jitScheduledTime.split(':').map(Number)
      const scheduledDateTime = new Date(jitScheduledDate)
      scheduledDateTime.setHours(hours, minutes, 0, 0)
      
      await reviewService.createJITCheckin({
        userId: employeeId,
        managerId,
        reviewType: jitReviewType,
        scheduledDate: scheduledDateTime,
        description: jitDescription || jitAgenda || undefined,
        initiatedBy: 'employee'  // Employee is the organizer for employee-initiated check-ins
      })

      toast.success('on-demand check-in request sent to your people leader')
      resetForm()
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error creating on-demand check-in:', error)
      toast.error(serverReason(error, 'Failed to create on-demand check-in request'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen)
      if (!isOpen) {
        resetForm()
      }
    }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 flex-shrink-0">
          <div>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2 text-white">
              <PlusCircle className="h-6 w-6" />
              Request On-Demand Check-in
            </DialogTitle>
            <DialogDescription className="text-orange-100 text-sm mt-1">
              Request an on-demand check-in with {managerName} for support or discussion
            </DialogDescription>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Reason / Description */}
          <div className="space-y-2">
            <Label htmlFor="jit-description" className="text-sm font-medium">
              Reason for Check-in <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="jit-description"
              placeholder="Why do you need this check-in? (e.g., 'Need guidance on Q1 priorities', 'Facing challenges with workload', 'Request support with impact & growth goals')..."
              value={jitDescription}
              onChange={(e) => setJitDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Preferred Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Preferred Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {jitScheduledDate ? format(jitScheduledDate, 'MMMM do, yyyy') : 'Select date...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={jitScheduledDate}
                  onSelect={setJitScheduledDate}
                  initialFocus
                  disabled={(date) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    return date < today
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preferred Time */}
          <div className="space-y-2">
            <Label htmlFor="jit-time" className="text-sm font-medium">
              Preferred Time <span className="text-red-500">*</span>
            </Label>
            <Select value={jitScheduledTime} onValueChange={setJitScheduledTime}>
              <SelectTrigger id="jit-time">
                <SelectValue placeholder="Select time..." />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {/* Business hours: 08:00 to 17:00 */}
                {Array.from({ length: 19 }, (_, i) => {
                  const hours = 8 + Math.floor(i / 2)
                  const minutes = i % 2 === 0 ? '00' : '30'
                  const value = `${String(hours).padStart(2, '0')}:${minutes}`
                  const display = `${hours > 12 ? hours - 12 : hours}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`
                  return (
                    <SelectItem key={value} value={value}>
                      {display}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="jit-duration" className="text-sm font-medium">
              Estimated Duration
            </Label>
            <Select value={String(jitDuration)} onValueChange={(val) => setJitDuration(Number(val))}>
              <SelectTrigger id="jit-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability Status */}
          {jitScheduledDate && jitScheduledTime && (
            <div className="space-y-2">
              {jitIsCheckingAvailability && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking availability...
                </div>
              )}
              {!jitIsCheckingAvailability && jitAvailabilityStatus === 'available' && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg border border-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  You and your people leader appear to be available
                </div>
              )}
              {!jitIsCheckingAvailability && jitAvailabilityStatus === 'conflict' && jitConflicts.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800 font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Potential scheduling conflicts detected
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Your people leader may have other commitments at this time. They will review your request and may suggest an alternative time.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label htmlFor="jit-agenda" className="text-sm font-medium">
              Topics to Discuss (Optional)
            </Label>
            <Textarea
              id="jit-agenda"
              placeholder="What would you like to discuss? Any preparation needed?..."
              value={jitAgenda}
              onChange={(e) => setJitAgenda(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex justify-end gap-3 flex-shrink-0 bg-white">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
              resetForm()
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-orange-600 hover:bg-orange-700"
            disabled={!jitScheduledDate || !jitScheduledTime || !jitDescription || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending Request...
              </>
            ) : (
              <>
                <PlusCircle className="h-4 w-4 mr-2" />
                Send Request to People Leader
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
